import API_BASE, { protectedApiFetch } from "../../api";
import { PUSH_ONBOARDING_CONSENT_VERSION } from "../../services/pushRegistrationsApi";
import { isCanonicalPushInstallationId } from "../../services/pushInstallation";

const REASONS = new Set(["eligible", "consent_recorded", "dismissed", "recently_shown"]);

export class NotificationOnboardingApiError extends Error {
  constructor(operation, { classification = "contract", status = null } = {}) {
    super("notification_onboarding_unavailable");
    this.name = "NotificationOnboardingApiError";
    this.code = "notification_onboarding_unavailable";
    this.operation = operation;
    this.classification = classification;
    this.status = status;
  }
}

function isAbortError(error, signal) {
  return Boolean(signal?.aborted || error?.name === "AbortError");
}

function httpCategory(status) {
  return Number.isInteger(status) ? `${Math.floor(status / 100)}xx` : "unknown";
}

function requestFailure(operation, classification, status = null) {
  const diagnostic = {
    operation,
    classification,
    httpCategory: classification === "http" ? httpCategory(status) : null,
    status: Number.isInteger(status) ? status : null,
  };
  console.warn("Notification onboarding request failed.", diagnostic);
  return new NotificationOnboardingApiError(operation, {
    classification,
    status: diagnostic.status,
  });
}

function isOptionalTimestamp(value) {
  return value === null || (typeof value === "string" && value.length > 0);
}

function installationBody(installationId, operation) {
  if (!isCanonicalPushInstallationId(installationId)) {
    throw requestFailure(operation, "contract");
  }
  return JSON.stringify({ installationId: installationId.toLowerCase() });
}

async function request(path, { method = "GET", signal, body, headers } = {}, operation) {
  let response;
  try {
    response = await protectedApiFetch(`${API_BASE}${path}`, {
      method,
      signal,
      body,
      headers,
    });
  } catch (error) {
    if (isAbortError(error, signal)) throw error;
    throw requestFailure(operation, "network");
  }
  if (!response.ok) throw requestFailure(operation, "http", response.status);
  try {
    return await response.json();
  } catch (error) {
    if (isAbortError(error, signal)) throw error;
    throw requestFailure(operation, "contract");
  }
}

export async function fetchPushOnboardingState({ installationId, signal } = {}) {
  if (!isCanonicalPushInstallationId(installationId)) {
    throw requestFailure("eligibility", "contract");
  }
  const payload = await request(
    `/notification-preferences/onboarding?installationId=${encodeURIComponent(
      installationId.toLowerCase()
    )}`,
    { signal },
    "eligibility"
  );
  if (
    typeof payload?.eligible !== "boolean" ||
    typeof payload?.pushEnabled !== "boolean" ||
    typeof payload?.consentRecorded !== "boolean" ||
    !REASONS.has(payload?.reason) ||
    payload?.consentVersion !== PUSH_ONBOARDING_CONSENT_VERSION ||
    !isOptionalTimestamp(payload?.lastShownAt) ||
    !isOptionalTimestamp(payload?.dismissedAt) ||
    !isOptionalTimestamp(payload?.nextEligibleAt)
  ) {
    throw requestFailure("eligibility", "contract");
  }
  return payload;
}

export async function markPushOnboardingShown({ installationId, signal } = {}) {
  const payload = await request(
    "/notification-preferences/onboarding/shown",
    {
      method: "POST",
      signal,
      headers: { "Content-Type": "application/json" },
      body: installationBody(installationId, "shown"),
    },
    "shown"
  );
  if (typeof payload?.shown !== "boolean" || !isOptionalTimestamp(payload?.shownAt)) {
    throw requestFailure("shown", "contract");
  }
  return payload;
}

export async function dismissPushOnboarding({ installationId, signal } = {}) {
  const payload = await request(
    "/notification-preferences/onboarding/dismiss",
    {
      method: "POST",
      signal,
      headers: { "Content-Type": "application/json" },
      body: installationBody(installationId, "dismiss"),
    },
    "dismiss"
  );
  if (
    typeof payload?.dismissed !== "boolean" ||
    !isOptionalTimestamp(payload?.dismissedAt) ||
    !isOptionalTimestamp(payload?.nextEligibleAt)
  ) {
    throw requestFailure("dismiss", "contract");
  }
  return payload;
}
